<template>
  <div>
    <div v-if="!readonly" class="cfs-field d-flex flex-column static-bg" :class="selectClass">
      <p v-show="label" class="input-title px-1 pb-1" :class="{ disabled }">
        {{ label }}
        <span v-if="optional" class="text-blue-havelock">(Optional)</span>
      </p>
      <v-select
        dense
        :outlined="outlined"
        :items="computedItems"
        v-bind="{ ...$attrs, ...$props }"
        :placeholder="placeholder || $t('SelectOption')"
        :label="null"
        :multiple="multiple"
        :menu-props="{ offsetY: true, ...menuProps }"
        :item-value="itemValue"
        :item-text="itemText"
        :class="propClass"
        v-on="$listeners"
      >
        <template v-if="isLoading" #append>
          <v-progress-circular :size="20" color="primary" indeterminate />
        </template>
        <template v-for="(_, slotName) in $scopedSlots" #[slotName]="scope">
          <slot :name="slotName" v-bind="scope" />
        </template>
        <template #prepend-item>
          <slot name="prepend-item" />
        </template>
      </v-select>
      <small v-if="disabledHint.length && disabled" class="pl-2 text-blue-havelock">
        * {{ disabledHint }}
      </small>
    </div>
    <CfsReadonlyField v-else v-bind="$props" :data="computedViewValue" />
  </div>
</template>

<script lang="ts">
import { isObject } from '@/helpers/objects';
import { computed, defineAsyncComponent, defineComponent, PropType } from 'vue';

export default defineComponent({
  components: {
    CfsReadonlyField: defineAsyncComponent(
      () => import('@/framework/inputs/special/CfsReadonlyFieldComponent.vue')
    ),
  },
  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    propClass: {
      type: String,
      default: '',
    },
    selectClass: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    menuProps: {
      type: Object,
      default: () => {},
    },
    placeholder: {
      type: String,
      default: '',
    },
    items: {
      type: Array as PropType<any[]>,
      required: true,
      default: () => [],
    },
    itemValue: {
      type: String,
      default: 'code',
    },
    itemText: {
      type: [String, Function],
      default: 'description',
    },
    /**when set to false fills input with application background solid color instead of inherited one.
     * I.e. removes transparency from input*/
    staticBg: {
      type: Boolean,
      default: true,
    },
    returnObject: {
      type: Boolean,
      default: true,
    },
    value: {
      type: [Object, String, Array] as PropType<
        Record<string, unknown> | string | unknown[] | null
      >,
      default: null,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    optional: {
      type: Boolean,
      default: false,
    },
    disabledHint: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const computedViewValue = computed(() => {
      if (!props.value) {
        return null;
      }
      if (typeof props.itemText === 'function') {
        return props.itemText(props.value);
      }

      const item = props.items?.find((item) => {
        if (isObject(props.value)) {
          return (
            item[props.itemValue] === (props.value as Record<string, unknown>)[props.itemValue]
          );
        }
        return item[props.itemValue] === props.value;
      });

      if (!item) {
        // @ts-ignore
        return props.value[props.itemText];
      }

      return item ? item[props.itemText] : props.value;
    });

    const computedItems = computed(() => {
      const items = props.items.slice();
      try {
        if (typeof props.itemText === 'string') {
          return items.sort((a, b) => a?.[props.itemText]?.localeCompare(b?.[props.itemText]));
        }
        if (typeof props.itemText === 'function') {
          return items.sort((a, b) => props.itemText(a)?.localeCompare(props.itemText(b)));
        }
      } catch (e) {
        console.log(items);
      }

      return items;
    });
    return { computedViewValue, computedItems };
  },
});
</script>
